import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Text,
} from '@chakra-ui/react'
import type { AxiosError, AxiosResponse } from 'axios'
import type { InsurerOrganizationUnitDiscount } from 'BrokerTsLib/src/dtos'
import type { InsuranceCategoryDiscount } from 'brokertslib/src/dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import type {
  FieldPath,
  FieldValues,
  PathValue
} from 'BrokerTsLib/src/rhf'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

import { FormLabel } from '../../components/FormLabel'
import { Loading } from '../../components/Loading'
import { SectionHeading } from '../shared/SectionHeading'

export const DiscountEditForm = ({
  insuranceCategoryDiscountId,
  title,
  name,
  defaultDiscount,
  maxDiscount,
  hiddenDiscount,
  unit,
  valid,
  insurerMinDiscount,
  insurerMaxDiscount,
  conflictDiscounts,
  onSubmit,
  onChanged,
}: {
  insuranceCategoryDiscountId: number
  title?: string
  name: string
  defaultDiscount?: number
  maxDiscount?: number
  hiddenDiscount?: number
  unit: string
  valid: boolean
  insurerMinDiscount?: number
  insurerMaxDiscount?: number
  conflictDiscounts: (
    | InsurerOrganizationUnitDiscount
    | InsuranceCategoryDiscount
  )[]
  onSubmit: (
    value: InsurerOrganizationUnitDiscount,
  ) => Promise<AxiosResponse | AxiosError>
  onChanged?: (
    isDirty: boolean,
    discount: InsurerOrganizationUnitDiscount,
  ) => void
}) => {
  const methods = useForm<InsurerOrganizationUnitDiscount>({
    defaultValues: {
      insuranceCategoryDiscountId,
      name,
      defaultDiscount,
      maxDiscount,
      hiddenDiscount,
      valid,
      unit,
      insurerMaxDiscount,
      insurerMinDiscount,
    },
    mode: 'all',
  })
  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting },
    reset,
  } = methods

  const maxDiscountValue = watch('maxDiscount')
  const values = watch()

  useEffect(() => onChanged?.(isDirty, values), [isDirty, values])

  const submitHandler = (data: InsurerOrganizationUnitDiscount) =>
    onSubmit(data).then(() => reset(data))

  return isSubmitting ? (
    <Loading large height={'44'} />
  ) : (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
      }}
    >
      <Flex sx={{ w: '100%', justifyContent: 'space-between' }}>
        {title && <SectionHeading>{title}</SectionHeading>}
      </Flex>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <Input type="hidden" {...register('insuranceCategoryDiscountId')} />
          <Input type="hidden" {...register('unit')} />

          <Flex sx={{ gap: 4, placeItems: 'start', w: '100%' }}>
            <FormControl isReadOnly>
              <FormLabel>Název slevy</FormLabel>

              <InputGroup>
                <Input {...register('name')} />
              </InputGroup>
            </FormControl>

            <DiscountNumberInput
              name="maxDiscount"
              label="Maximální"
              unit={unit}
              max={insurerMaxDiscount ?? 100}
              min={insurerMinDiscount ?? 0}
            />

            <DiscountNumberInput
              name="defaultDiscount"
              label="Výchozí"
              helperText={`Min: ${insurerMinDiscount} ${unit}, Max: ${insurerMaxDiscount} ${unit}`}
              unit={unit}
              max={maxDiscountValue}
              min={insurerMinDiscount ?? 0}
            />

            <DiscountNumberInput
              name="hiddenDiscount"
              label="Skrytá"
              unit={unit}
              max={(insurerMaxDiscount ?? 0) - maxDiscountValue}
              min={0}
            />

            <FormControl mx="auto">
              <FormLabel>Povolená</FormLabel>

              <InputGroup sx={{ mt: 1, ml: 1 }}>
                <Switch size="lg" {...register('valid')} />
              </InputGroup>
            </FormControl>
          </Flex>
          <Flex
            sx={{
              gap: 4,
              alignItems: 'baseline',
              flexWrap: 'wrap',
              w: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Button
              colorScheme="primary"
              type="submit"
              sx={{ mb: 4 }}
              isDisabled={!isDirty}
            >
              Uložit slevu
            </Button>
            {(conflictDiscounts?.length ?? 0) > 0 && (
              <ConflictDiscounts
                conflicts={conflictDiscounts.map((x) => x.name)}
              />
            )}
          </Flex>
        </form>
      </FormProvider>
    </Box>
  )
}

const ConflictDiscounts = ({ conflicts }: { conflicts: string[] }) => {
  return (
    <Flex fontSize={'sm'} whiteSpace={'pre-wrap'}>
      <Text>Nelze kombinovat s:</Text>
      {conflicts.map((conflict, index) => (
        <Text key={index}>
          {` ${conflict}`}
          {index < conflicts.length - 1 && ','}
        </Text>
      ))}
    </Flex>
  )
}

const DiscountNumberInput = function <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helperText,
  unit,
  max,
  min,
}: {
  name: TName
  label: string
  helperText?: string
  unit: string
  max: number | undefined
  min: number | undefined
}) {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<TFieldValues>()

  const minActual = min ?? 0
  const maxActual = max ?? 100

  const value = watch(name)

  useEffect(() => {
    if (value > maxActual)
      setValue(name, maxActual as PathValue<TFieldValues, TName>)
  }, [max])

  useEffect(() => {
    if (value < minActual)
      setValue(name, minActual as PathValue<TFieldValues, TName>)
  }, [min])

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        min: minActual,
        max: maxActual,
      }}
      render={({ field: { ref, onChange, ...restField } }) => (
        <FormControl isRequired isInvalid={!!errors.maxDiscount}>
          <FormLabel>{label}</FormLabel>
          <InputGroup>
            <InputLeftElement pointerEvents="none" color="black" fontSize="md">
              {unit}
            </InputLeftElement>
            <NumberInput
              w="120px"
              {...restField}
              onChange={(_, valueAsNumber) => onChange(valueAsNumber)}
              min={minActual}
              max={maxActual}
            >
              <NumberInputField pl={8} ref={ref} name={restField.name} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </InputGroup>

          {helperText && (
            <FormHelperText
              sx={{
                mt: -1.5,
                width: '100%',
                maxW: '120px',
                textAlign: 'center',
              }}
            >
              {helperText}
            </FormHelperText>
          )}
          <FormErrorMessage>
            {errors[name]?.message?.toString()}
          </FormErrorMessage>
        </FormControl>
      )}
    />
  )
}
